import { BasicInfo } from 'components/SearchSideBar/BasicInfo';
import { ModalMasterData } from 'components/SearchSideBar/BasicInfo/types';
import { SearchSideBarLayout } from 'components/SearchSideBar/SearchSidebarLayout';
import { FC } from 'react';
import { getOldestDayWdtno } from 'components/SearchSideBar/logics/getOldestDayWdtno';
import { useSearchCondition } from 'components/SearchSideBar/hooks/useSearchCondition';
import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { useShowOverlay } from 'components/SearchSideBar/hooks/useShowOverlay';
import { useCountUpdateAnimation } from 'hooks/useCountUpdateAnimation';
import { PickUpConditions } from 'components/SearchSideBar/PickUpCondition';
import { SearchKeyword } from 'components/SearchSideBar/SearchKeyword';
import { Details } from 'components/SearchSideBar/Details';
import { SearchButtonArea } from 'components/SearchSideBar/SearchButtonArea';
import { OverlaySearchArea } from 'components/SearchSideBar/OverlaySearchArea';
import { useSearch } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/hooks/useSearch';
import { ModalOverlay } from 'components/ModalOverlay';
import { createIndustryDisplayData } from 'components/IndustryModal/logics/createIndustryDisplayData';
import {
  createDisplayArea,
  createDisplayCity,
} from 'components/LocationStationModal/LocationModal/logics/createLocationDisplayData';
import { createDisplayStation } from 'components/LocationStationModal/StationModal/logics/createStationDisplayData';
import { useStationModal } from 'components/LocationStationModal/StationModal/hooks/useStationModal';
import { createOccupationDisplayData } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import { useEachModalJobCount } from 'hooks/useEachModalJobCount';
import dynamic from 'next/dynamic';
import { SideBarProps } from './types';
import { CurrentCondition } from '../CurrentCondition';

const OccupationModal = dynamic(
  () =>
    import('components/OccupationSelectModal').then((module) => ({
      default: module.OccupationModal,
    })),
  { ssr: false }
);
const LocationModal = dynamic(
  () =>
    import('components/LocationStationModal/LocationModal').then((module) => ({
      default: module.LocationModal,
    })),
  { ssr: false }
);
const StationModal = dynamic(
  () =>
    import('components/LocationStationModal/StationModal').then((module) => ({
      default: module.StationModal,
    })),
  { ssr: false }
);
const IndustryModal = dynamic(
  () =>
    import('components/IndustryModal').then((module) => ({
      default: module.IndustryModal,
    })),
  { ssr: false }
);

/**
 * Propsの型定義
 * @param sideBarProps サイドバーで使用するデータ
 * @param modalMasterData モーダルで使用するマスタデータ
 * @param isRegistSuggestionBannerDisplay Is the bottom banner display?
 */
interface Props {
  sideBarProps: SideBarProps;
  modalMasterData: ModalMasterData;
  isRegistSuggestionBannerDisplay?: boolean;
}

export const SearchSideBar: FC<Props> = ({
  sideBarProps,
  modalMasterData,
  isRegistSuggestionBannerDisplay,
}) => {
  const {
    savedSearches,
    isLogin,
    initialSideBarMasterData,
    initialJobCountData,
    searchConditionLabel,
    currentConditionData,
    drillDown,
  } = sideBarProps;
  const oldestDayWdtno = getOldestDayWdtno(savedSearches);
  const {
    occupation,
    location,
    routeStation,
    industry,
    employmentStatus,
    income,
    includeSearchKeyword,
    excludeSearchKeyword,
    pickUpCondition,
    searchDetail,
    updateJobCount,
    totalCount,
    modalTotalCount,
    selectedCurrentCondition,
    updateModalJobCount,
    isConditionUpperLimitError,
  } = useSearchCondition({
    initialSideBarMasterData,
    initialDrillDown: drillDown,
    initialJobCountData,
  });

  const occupationModal = useDisclosure();
  const industryModal = useDisclosure();
  const locationModal = useDisclosure();

  const {
    occupationModalJobCount,
    locationAndStationModalJobCount,
    industryModalJobCount,
    getJobCountWithoutOccupation,
    getJobCountWithoutLocationAndStation,
    getJobCountWithoutIndustry,
  } = useEachModalJobCount(selectedCurrentCondition);

  const {
    stationModal,
    routeMaster,
    onOpenRouteModal,
    reloadRouteMaster,
    checkedStationState,
    checkedStationDispatch,
  } = useStationModal({
    selectedCurrentCondition,
    locationAndStationModalJobCount,
    areaMaster: modalMasterData.area,
  });

  const {
    onClickSearchButton,
    onClickSearchFloatButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRouteStationModalSearchButton,
    onClickIndustryModalSearchButton,
  } = useSearch(selectedCurrentCondition, oldestDayWdtno, isLogin);

  const { isOpacity, isInvisible, onChangeIsShowOverlay, observerRef } =
    useShowOverlay();

  const countAnimationRef = useCountUpdateAnimation(totalCount);

  return (
    <>
      <SearchSideBarLayout isScrollButtonUp={isRegistSuggestionBannerDisplay}>
        <CurrentCondition
          isLogin={isLogin}
          savedSearchCount={savedSearches.length}
          searchConditionLabel={
            searchConditionLabel !== '' ? searchConditionLabel : undefined
          }
          selectedCurrentCondition={selectedCurrentCondition}
          currentConditionData={currentConditionData}
        />
        <BasicInfo
          conditions={{
            occupation,
            location,
            routeStation,
            industry,
            employmentStatus,
            income,
          }}
          modalAction={{
            onOpenOccupationModal: occupationModal.onOpen,
            onOpenLocationModal: locationModal.onOpen,
            onOpenRouteModal,
            onOpenIndustryModal: industryModal.onOpen,
          }}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
          getJobCountAtModalDisplay={{
            getJobCountWithoutOccupation,
            getJobCountWithoutLocationAndStation,
            getJobCountWithoutIndustry,
          }}
        />
        <PickUpConditions
          list={pickUpCondition.pickUpConditionDisplayList}
          updateIsCheckedIsNewArrival={
            pickUpCondition.updateIsCheckedIsNewArrival
          }
          updateIsCheckedIsNearDeadline={
            pickUpCondition.updateIsCheckedIsNearDeadline
          }
          updateIsCheckedHolidayAndWayOfWorkingList={
            searchDetail.holidayAndWayOfWorking
              .updateIsCheckedHolidayAndWayOfWorkingList
          }
          updateIsCheckedRequirementList={
            searchDetail.requirements.updateIsCheckedRequirementList
          }
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchKeyword
          include={includeSearchKeyword}
          exclude={excludeSearchKeyword}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <Details
          searchDetail={searchDetail}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchButtonArea
          onClickSearchButton={onClickSearchButton}
          observerRef={observerRef}
          countAnimationRef={countAnimationRef}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
        <OverlaySearchArea
          totalCount={totalCount}
          isOpacity={isOpacity}
          isInvisible={isInvisible}
          onClickSearchButton={onClickSearchFloatButton}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
      </SearchSideBarLayout>
      <ModalOverlay
        show={
          occupationModal.isOpen ||
          locationModal.isOpen ||
          stationModal.isOpen ||
          industryModal.isOpen
        }
        onClickOverlay={() => {
          occupationModal.onClose();
          locationModal.onClose();
          stationModal.onClose();
          industryModal.onClose();
        }}
      >
        {occupationModal.isOpen && (
          <OccupationModal
            show={occupationModal.isOpen}
            onClickClose={occupationModal.onClose}
            onClickSearch={(checkedState) => {
              onClickOccupationModalSearchButton({
                searchJobTypeLId: checkedState.L,
                searchJobTypeMId: checkedState.M,
                searchJobTypeSId: checkedState.S,
              });
            }}
            onChangeSelectedOccupation={(checkedState) => {
              updateModalJobCount({
                searchJobTypeLId: checkedState.L,
                searchJobTypeMId: checkedState.M,
                searchJobTypeSId: checkedState.S,
              });
            }}
            displayOccupationData={createOccupationDisplayData(
              modalMasterData.occupation,
              occupationModalJobCount
            )}
            selectedOccupationIdList={{
              L: selectedCurrentCondition.searchJobTypeLId,
              M: selectedCurrentCondition.searchJobTypeMId,
              S: selectedCurrentCondition.searchJobTypeSId,
            }}
            matchJobCount={modalTotalCount}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
        {locationModal.isOpen && (
          <LocationModal
            show={locationModal.isOpen}
            onClickClose={locationModal.onClose}
            initialTotalCount={modalTotalCount}
            selectedLocationModalIdList={{
              checkedArea: selectedCurrentCondition.areaId,
              checkedPref: selectedCurrentCondition.prefectureId,
              checkedCity: selectedCurrentCondition.cityId,
              checkedWard: selectedCurrentCondition.wardId,
            }}
            areaList={createDisplayArea(
              modalMasterData.area,
              locationAndStationModalJobCount
            )}
            cityList={createDisplayCity(
              modalMasterData.area,
              locationAndStationModalJobCount
            )}
            onClickSearch={(checkedState) => {
              onClickLocationModalSearchButton({
                areaId: checkedState.checkedAllArea,
                prefectureId: checkedState.checkedAllPref,
                cityId: checkedState.checkedAllCity,
                wardId: checkedState.checkedWard,
              });
            }}
            onChangeSelectedLocation={(checkedState) => {
              updateModalJobCount({
                areaId: checkedState.checkedAllArea,
                prefectureId: checkedState.checkedAllPref,
                cityId: checkedState.checkedAllCity,
                wardId: checkedState.checkedWard,
              });
            }}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
        {stationModal.isOpen && (
          <StationModal
            show={stationModal.isOpen}
            onClickClose={stationModal.onClose}
            initialTotalCount={modalTotalCount}
            areaList={createDisplayArea(
              modalMasterData.area,
              locationAndStationModalJobCount
            )}
            railwayCompanyList={createDisplayStation(
              routeMaster,
              locationAndStationModalJobCount
            )}
            onClickSearch={(checkedState) => {
              onClickRouteStationModalSearchButton({
                routeId: checkedState.checkedAllRoute,
                stationId: checkedState.checkedStation,
              });
            }}
            onChangeSelectedStation={(checkedState) => {
              updateModalJobCount({
                routeId: checkedState.checkedAllRoute,
                stationId: checkedState.checkedStation,
              });
            }}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
            dispatch={checkedStationDispatch}
            checkedState={checkedStationState}
            reloadRouteMaster={reloadRouteMaster}
          />
        )}
        {industryModal.isOpen && (
          <IndustryModal
            show={industryModal.isOpen}
            onClickClose={industryModal.onClose}
            onClickSearch={(checkedState) => {
              onClickIndustryModalSearchButton({
                industryLId: checkedState.L,
                industrySId: checkedState.S,
              });
            }}
            onChangeSelectedIndustry={(checkedState) => {
              updateModalJobCount({
                industryLId: checkedState.L,
                industrySId: checkedState.S,
              });
            }}
            displayIndustryData={createIndustryDisplayData(
              modalMasterData.industry,
              industryModalJobCount
            )}
            selectedIndustryIdList={{
              L: selectedCurrentCondition.industryLId,
              S: selectedCurrentCondition.industrySId,
            }}
            matchJobCount={modalTotalCount}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
      </ModalOverlay>
    </>
  );
};
